import { Clock3 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Menu, Image, Text, Group, Avatar, Stack, ActionIcon, Divider } from '@mantine/core';
import { getRecentGames, clearRecentGames, gameClicked } from '@/utils/event';
import { useRouter } from 'next/router';

// scss
import styles from './rencentPlay.module.scss';
import { setCurrentGame } from '@/store/features/gameSlice';
import { useDispatch } from 'react-redux';

export default function RecentPlay() {
    const [recentGames, setRecentGames] = useState([]);
    const [opened, setOpened] = useState(false);
    const dispatch = useDispatch();
    const router = useRouter();

    // 格式化时间显示
    const formatTime = (timestamp) => {
        const now = Date.now();
        const diff = now - timestamp;
        
        const minute = 60 * 1000;
        const hour = 60 * minute;
        const day = 24 * hour;
        
        if (diff < minute) {
            return 'Just Now';
        } else if (diff < hour) {
            return `${Math.floor(diff / minute)} minutes ago`;
        } else if (diff < day) {
            return `${Math.floor(diff / hour)} hours ago`;
        } else {
            return `${Math.floor(diff / day)} days ago`;
        }
    };

    // 加载历史记录
    const loadRecentGames = () => {
        const games = getRecentGames();
        setRecentGames(games);
    };

    // 处理游戏点击
    const handleGameClick = (game) => {
        if (window) {
            window.gtag?.('event', 'click_game');
            window.gtag('event', 'conversion', {
                'send_to': 'AW-17085889980/P17JCLv42coaELz7l9M_'
            });
        }
        gameClicked(game);
        dispatch(setCurrentGame({...game}));
        setOpened(false);
        router.push(`/game/${game.slug}`);
    };

    // 清空历史记录
    const handleClearHistory = () => {
        clearRecentGames();
        setRecentGames([]);
        setOpened(false);
    };

    // 监听历史记录更新事件
    useEffect(() => {
        // 初始加载
        loadRecentGames();
        
        // 监听自定义事件
        const handleRecentGamesUpdate = (event) => {
            setRecentGames(event.detail);
        };
        
        window.addEventListener('recentGamesUpdated', handleRecentGamesUpdate);
        
        return () => {
            window.removeEventListener('recentGamesUpdated', handleRecentGamesUpdate);
        };
    }, []);

    return (
        <Menu
            opened={opened}
            onChange={setOpened}
            position="bottom-end"
            width={320}
            shadow="md"
            styles={{
                dropdown: {
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    maxHeight: '400px',
                    overflowY: 'auto'
                }
            }}
        >
            <Menu.Target>
                <ActionIcon
                    variant="transparent"
                    size="lg"
                    onClick={() => setOpened(!opened)}
                    className={styles.recent_play}
                >
                    <Clock3 color={'#ffffff'} size={24} />
                </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
                <Menu.Label style={{ color: '#230000', fontSize: '14px', fontWeight: 'bold' }}>
                    Recent Played
                </Menu.Label>
                
                {recentGames.length === 0 ? (
                    <Menu.Item disabled style={{ color: '#888' }}>
                        <Text size="sm">No Data</Text>
                    </Menu.Item>
                ) : (
                    <>
                        {recentGames.map((game, index) => (
                            <Menu.Item
                                key={game.id}
                                onClick={() => handleGameClick(game)}
                                style={{
                                    padding: '12px',
                                    borderRadius: '8px',
                                    margin: '4px 0'
                                }}
                                className={styles.menu_item}
                            >
                                <Group gap="sm" wrap="nowrap">
                                    {/* 游戏封面 */}
                                    <Avatar
                                        src={game.cover_image}
                                        size={40}
                                        radius="md"
                                        style={{
                                            backgroundColor: '#f5f5f5',
                                            border: 'none'
                                        }}
                                    />
                                    
                                    {/* 游戏信息 */}
                                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                        <Text
                                            size="sm"
                                            fw={500}
                                            c="#230000"
                                            truncate
                                            style={{ lineHeight: 1.2 }}
                                        >
                                            {game.name}
                                        </Text>
                                        <Text
                                            size="xs"
                                            c="dimmed"
                                            style={{ lineHeight: 1.2 }}
                                        >
                                            {formatTime(game.timestamp)}
                                        </Text>
                                    </Stack>
                                </Group>
                            </Menu.Item>
                        ))}
                        
                        {recentGames.length > 0 && (
                            <Menu.Item
                                onClick={handleClearHistory}
                                style={{
                                    color: '#ff6b6b',
                                    fontSize: '12px',
                                    textAlign: 'center',
                                    padding: '8px',
                                    marginTop: '8px'
                                }}
                            >
                                Clean History
                            </Menu.Item>
                        )}
                    </>
                )}
            </Menu.Dropdown>
        </Menu>
    );
}